import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { addToCart } from '../utils/cart';
import { openWhatsAppInquiry } from '../utils/inquiry';
import { toast } from 'sonner';
import { normalizeImages } from '../utils/imageUtils'; // ✅ HEIC fix

const ProductCard = ({ product, index }) => {
  const [imageIdx, setImageIdx] = useState(0);
  // ✅ normalizeImages converts HEIC URLs to browser-safe f_auto URLs
  const images = normalizeImages(product.images);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Added to cart!');
  };

  const handleWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openWhatsAppInquiry({
      productSku: product.sku,
      productCategory: product.category,
      productTitle: product.title
    });
  };

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageIdx((imageIdx + 1) % images.length);
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageIdx((imageIdx - 1 + images.length) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/product/${product.sku}`} className="product-card p-4 group block" data-testid={`product-card-${product.sku}`}>
        <div className="relative overflow-hidden mb-3 aspect-[3/4]">
          <img
            src={images[imageIdx]}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg';
            }}
          />

          {['3XL', '4XL', '5XL'].includes(product.subcategory) && (
            <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded z-10">
              {product.subcategory}
            </span>
          )}

          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`prev-${product.sku}`}
              >
                <ChevronLeft className="w-4 h-4 text-[#2A0000]" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`next-${product.sku}`}
              >
                <ChevronRight className="w-4 h-4 text-[#2A0000]" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {images.map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === imageIdx ? 'bg-white' : 'bg-white/50'}`} />
                ))}
              </div>
            </>
          )}
        </div>

        <h3 className="font-semibold text-[#2C2C2C] mb-1">{product.title}</h3>
        <p className="text-sm text-gray-600 mb-1" data-testid="product-sku">{product.sku}</p>
        <p className="text-sm text-gray-600 mb-4">Color: {product.color}</p>

        <div className="space-y-2">
          <button
            onClick={handleWhatsApp}
            className="block w-full bg-[#25D366] text-white text-center py-2 rounded-sm hover:bg-[#20BA5A] transition-colors text-sm font-medium"
            data-testid="whatsapp-inquiry-button"
          >
            Inquire on WhatsApp
          </button>
          <button onClick={handleAddToCart} className="w-full btn-outline py-2 text-sm">
            Add to Wishlist
          </button>
        </div>
      </Link>
    </motion.div>
  );
};

const CollectionsPage = () => {
  const { category, subcategory: subcategoryParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const activeSubSlug = searchParams.get('subcategory') || subcategoryParam || null;

  const [products, setProducts]           = useState([]);
  const [categoryData, setCategoryData]   = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [activeSubName, setActiveSubName] = useState('');
  const [sortBy, setSortBy]               = useState('newest');
  const [loading, setLoading]             = useState(true);

  useEffect(() => { fetchData(); }, [category, activeSubSlug]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const catResponse = await api.get('/categories');
      const cat = catResponse.data.find(c => c.slug === category);
      setCategoryData(cat);

      const subResponse = await api.get(`/subcategories?category_slug=${category}`);
      const subs = subResponse.data || [];
      setSubcategories(subs);

      let resolvedSubName = '';
      if (activeSubSlug) {
        const matchedSub = subs.find(s => s.slug === activeSubSlug);
        if (matchedSub) {
          resolvedSubName = matchedSub.name;
          setActiveSubName(matchedSub.name);
        } else {
          resolvedSubName = activeSubSlug;
          setActiveSubName(activeSubSlug);
        }
      } else {
        setActiveSubName('');
      }

      let query = '/products?status=published';
      if (cat) query += `&category=${encodeURIComponent(cat.name)}`;
      if (resolvedSubName) query += `&subcategory=${encodeURIComponent(resolvedSubName)}`;

      const prodResponse = await api.get(query);
      setProducts(prodResponse.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubFilter = (subSlug) => {
    if (!subSlug) {
      if (subcategoryParam) { navigate(`/collections/${category}`); }
      else { setSearchParams({}); }
    } else {
      setSearchParams({ subcategory: subSlug });
    }
  };

  const sortedProducts = [...products];

  const isBigSize = ['3xl', '4xl', '5xl'].includes(activeSubSlug);

  return (
    <div className="min-h-screen bg-[#FFFDF9] py-8">
      <div className="container mx-auto px-4 md:px-8">

        <nav className="text-sm mb-8" data-testid="breadcrumb">
          <Link to="/" className="text-[#E5A93C] hover:underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          {category && (
            <>
              <Link
                to={`/collections/${category}`}
                className="text-[#E5A93C] hover:underline"
                onClick={() => setSearchParams({})}
              >
                {categoryData?.name || category}
              </Link>
              {activeSubSlug && activeSubName && (
                <>
                  <span className="mx-2 text-gray-400">/</span>
                  <span className="text-gray-600">{activeSubName}</span>
                </>
              )}
            </>
          )}
        </nav>

        <div className="mb-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#2A0000] mb-2">
            {categoryData?.name || 'All Collections'}
            {activeSubName && (
              <span className="text-2xl md:text-3xl text-[#E5A93C] ml-3">— {activeSubName}</span>
            )}
          </h1>
          {isBigSize && (
            <p className="text-sm text-amber-700 font-medium mt-1">Big Size Collection</p>
          )}
          {categoryData?.description && !activeSubSlug && (
            <p className="text-[#2C2C2C] text-lg mt-2">{categoryData.description}</p>
          )}
        </div>

        {subcategories.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-500 mr-1">
              {subcategories.some(s => ['3xl','4xl','5xl'].includes(s.slug)) ? 'Filter by size:' : 'Filter:'}
            </span>
            <button
              onClick={() => handleSubFilter(null)}
              className={`px-5 py-2 rounded-sm text-sm font-medium border transition-colors ${
                !activeSubSlug
                  ? 'bg-[#2A0000] text-white border-[#2A0000]'
                  : 'bg-white text-gray-600 border-[#E5A93C]/30 hover:bg-[#E5A93C] hover:text-white hover:border-[#E5A93C]'
              }`}
            >
              All {categoryData?.name}
            </button>
            {subcategories.map(sub => {
              const isActive = activeSubSlug === sub.slug;
              const isBig = ['3xl', '4xl', '5xl'].includes(sub.slug);
              return (
                <button
                  key={sub.slug}
                  onClick={() => handleSubFilter(sub.slug)}
                  className={`px-5 py-2 rounded-sm text-sm font-bold border transition-colors ${
                    isActive
                      ? 'bg-[#2A0000] text-white border-[#2A0000]'
                      : isBig
                        ? 'bg-white text-[#2A0000] border-[#2A0000] hover:bg-[#2A0000] hover:text-white'
                        : 'bg-white text-gray-600 border-[#E5A93C]/30 hover:bg-[#E5A93C] hover:text-white hover:border-[#E5A93C]'
                  }`}
                >
                  {sub.name}
                </button>
              );
            })}
          </div>
        )}

        {activeSubSlug && activeSubName && (
          <div className="mb-6 flex items-center justify-between bg-amber-50 border border-amber-200 rounded px-4 py-2.5">
            <p className="text-sm text-amber-800">
              Showing <span className="font-bold">{activeSubName}</span> in{' '}
              <span className="font-bold">{categoryData?.name}</span>
              {!loading && (
                <span className="ml-1 text-amber-600">
                  — {sortedProducts.length} product{sortedProducts.length !== 1 ? 's' : ''}
                </span>
              )}
            </p>
            <button
              onClick={() => handleSubFilter(null)}
              className="text-xs text-amber-700 underline hover:text-amber-900 ml-4 flex-shrink-0"
            >
              Clear filter
            </button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : sortedProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg mb-4">
              {activeSubName
                ? `No ${activeSubName} size products added yet.`
                : 'No products found in this category.'}
            </p>
            {activeSubSlug && (
              <button onClick={() => handleSubFilter(null)} className="btn-primary px-6 py-2">
                View all {categoryData?.name}
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {sortedProducts.map((product, index) => (
              <ProductCard key={product.sku} product={product} index={index} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default CollectionsPage;